// Login gate + user badge. main.ts calls checkAuth() before anything else
// boots; if it fails, showLoginScreen() takes over and only calls back into
// the rest of bootstrap once a real session exists.
import { ApiError, getCurrentUser, login, logout } from "./api";
import { inviteTokenFromUrl, showInvite, showRequestAccess } from "./onboarding";
import { authFailureKind, isTransientError, withRetry } from "./bootRetry";
import { getState, setState } from "./state";

/** "authed": a session; "login": no session (401) - show the form;
 * "unreachable": the server couldn't be asked (2026-10-08: a request lost
 * in a network change used to land here as "not logged in", throwing a
 * valid session away). /api/auth/me is retried with backoff first; the
 * service worker also answers it from its cached copy when slow/offline. */
export type AuthCheck = "authed" | "login" | "unreachable";

export async function checkAuth(): Promise<AuthCheck> {
  try {
    const user = await withRetry(() => getCurrentUser(), { attempts: 4, baseMs: 500, maxMs: 4000 });
    setState({ currentUser: user });
    return "authed";
  } catch (e) {
    setState({ currentUser: null });
    return authFailureKind(e);
  }
}

export function showLoginScreen(onSuccess: () => void): void {
  const screen = document.getElementById("login-screen")!;
  const form = document.getElementById("login-form") as HTMLFormElement;
  const usernameInput = document.getElementById("login-username") as HTMLInputElement;
  const passwordInput = document.getElementById("login-password") as HTMLInputElement;
  const errorEl = document.getElementById("login-error")!;

  const mfaRow = document.getElementById("login-mfa") as HTMLElement;
  const totpInput = document.getElementById("login-totp") as HTMLInputElement;

  screen.style.display = "flex";
  usernameInput.focus();

  // Onboarding (onboarding.ts): "Request access" link, and invite links
  // (/#invite=<token>) open the account setup screen straight away.
  document.getElementById("login-request-access")?.addEventListener("click", (e) => {
    e.preventDefault();
    showRequestAccess();
  });
  const invite = inviteTokenFromUrl();
  if (invite) {
    showInvite(invite, (user) => {
      setState({ currentUser: user });
      onSuccess();
    });
  }

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    errorEl.textContent = "";
    const submitBtn = form.querySelector("button[type=submit]") as HTMLButtonElement;
    submitBtn.disabled = true;
    const code = mfaRow.style.display === "none" ? undefined : totpInput.value.replace(/\s/g, "");
    login(usernameInput.value, passwordInput.value, code)
      .then((user) => {
        setState({ currentUser: user });
        screen.style.display = "none";
        onSuccess();
      })
      .catch((err: unknown) => {
        if (isTransientError(err)) {
          // not a wrong password: say so, and keep what they typed
          errorEl.textContent = navigator.onLine
            ? "Couldn't reach WRAP (the network may have just changed). Try again."
            : "This phone is offline. Sign in once you have signal.";
          return;
        }
        if (err instanceof ApiError && err.status === 401 && err.detail === "mfa_required") {
          // password was right; this account uses two-factor sign-in
          mfaRow.style.display = "";
          totpInput.value = "";
          totpInput.focus();
          errorEl.textContent = "Enter the 6-digit code from your authenticator app.";
          return;
        }
        if (err instanceof ApiError && err.status === 429) {
          errorEl.textContent = "Too many incorrect codes. Please wait 15 minutes and try again.";
          return;
        }
        if (mfaRow.style.display !== "none") {
          errorEl.textContent = "Incorrect username, password or code.";
          totpInput.value = "";
          totpInput.focus();
          return;
        }
        errorEl.textContent = "Incorrect username or password.";
        passwordInput.value = "";
        passwordInput.focus();
      })
      .finally(() => {
        submitBtn.disabled = false;
      });
  });
}

export function initUserBadge(): void {
  const info = document.getElementById("current-user-info")!;
  const logoutBtn = document.getElementById("logout-btn") as HTMLButtonElement;
  const user = getState().currentUser;
  if (user) {
    info.textContent = `${user.display_name} (${user.role})`;
    logoutBtn.style.display = "inline-block";
  }

  logoutBtn.addEventListener("click", () => {
    // field PWA: drop cached API responses (sw.js) so the next person on a shared phone sees nothing
    navigator.serviceWorker?.controller?.postMessage("clear-api-cache");
    void logout().finally(() => window.location.reload());
  });
}
